import type { SessionKey } from '@protontech/crypto';

import type { SHARE_MEMBER_STATE } from '@proton/shared/lib/drive/constants';
import type { SHARE_MEMBER_PERMISSIONS, SHARE_URL_PERMISSIONS } from '@proton/shared/lib/drive/permissions';
import type { LinkType } from '@proton/shared/lib/interfaces/drive/link';
import type { ThumbnailURLInfo } from '@proton/shared/lib/interfaces/drive/sharing';
import type { VolumeType } from '@proton/shared/lib/interfaces/drive/volume';

// Share type string used in metrics context, do not confuse with ShareType enum.
export type ShareTypeString = 'main' | 'device' | 'photo' | 'shared';
export type ShareTypeStringWithPublic = ShareTypeString | 'shared_public';

export enum ShareType {
    default = 1,
    standard = 2,
    device = 3,
    photos = 4,
}

export enum ShareState {
    active = 1,
    restored = 3,
}

export interface Share {
    shareId: string;
    rootLinkId: string;
    volumeId: string;
    creator: string;
    isLocked: boolean;
    isDefault: boolean;
    possibleKeyPackets: string[];
    type: ShareType;
    linkType: LinkType;
    state: ShareState;
    createTime: number;
    volumeType?: VolumeType;
}

export interface ShareWithKey extends Share {
    addressId: string;
    key: string;
    passphrase: string;
    passphraseSignature: string;
    rootLinkRecoveryPassphrase?: string;
    memberships: ShareMembership[];
}

export interface LockedVolumeForRestore {
    lockedVolumeId: string;
    defaultShares: LockedShareForRestore[];
    devices: LockedDeviceForRestore[];
    photos: LockedPhotosForRestore[];
}

interface LockedShareForRestore {
    shareId: string;
    linkDecryptedPassphrase: string;
}

interface LockedDeviceForRestore extends LockedShareForRestore {
    shareDecryptedPassphrase: string;
    shareSessionKey: SessionKey;
}
interface LockedPhotosForRestore extends LockedShareForRestore {
    shareDecryptedPassphrase: string;
    shareSessionKey: SessionKey;
}
export interface ShareMembership {
    memberId: string;
    shareId: string;
    addressId: string;
    addressKeyId: string;
    inviterEmail: string;
    createTime: number;
    modifyTime: number;
    permissions: SHARE_MEMBER_PERMISSIONS;
    state: SHARE_MEMBER_STATE;
    keyPacket: string;
    keyPacketSignature: string;
    sessionKeySignature: string;
    unlockable: boolean;
}

export interface ShareInvitation {
    invitationId: string;
    inviterEmail: string;
    inviteeEmail: string;
    permissions: SHARE_MEMBER_PERMISSIONS;
    keyPacket: string;
    keyPacketSignature: string;
    createTime: number;
    state: SHARE_MEMBER_STATE;
}

interface ShareInvitationShare {
    shareId: string;
    volumeId: string;
    passphrase: string;
    shareKey: string;
    creatorEmail: string;
}

interface ShareInvitationLink {
    linkId: string;
    name: string;
    mimeType: string;
    isFile: boolean;
    type: LinkType;
}

export interface ShareInvitationDetails {
    invitation: ShareInvitation;
    share: ShareInvitationShare;
    link: ShareInvitationLink;
    decryptedLinkName?: string;
}

export interface SharedUrlInfo {
    contentKeyPacket: string;
    linkId: string;
    linkType: LinkType;
    mimeType: string;
    name: string;
    nodeKey: string;
    nodeHashKey: string | null;
    nodePassphrase: string;
    nodePassphraseSignature: string;
    permissions: SHARE_URL_PERMISSIONS;
    shareKey: string;
    sharePassphrase: string;
    sharePasswordSalt: string;
    size: number;
    signatureEmail?: string;
    thumbnailUrlInfo: ThumbnailURLInfo;
    token: string;
}
